import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { db } from "../firebase";
import Ranking from "../components/Ranking";
import { Record } from "../config";
import { RootState } from "../rootReducer";
import { RouteComponentProps } from "react-router-dom";
import dayjs from "dayjs";

type State = {
  records: Record[];
  currentRecords: Record[];
  lastRecord: any;
  currentPage: number;
};

const ContainerRanking: React.FC<RouteComponentProps<{ category: string }>> = ({
  history,
  match,
}) => {
  const category = match.params.category;
  const limit = 6;
  const [state, setState] = useState<State>({
    records: [],
    currentRecords: [],
    lastRecord: undefined,
    currentPage: 1,
  });
  const { records, currentRecords, lastRecord, currentPage } = state;

  const board = useSelector((state: RootState) => state.board);
  const user = useSelector((state: RootState) => state.user);

  const prevDisabled: boolean = currentPage === 1;
  const nextDisabled: boolean =
    currentRecords.length <= 5 ||
    (!lastRecord && currentPage === Math.ceil(records.length / limit));

  //firestoreではdb.collection("records").where("level", "==", board.level).orderBy("createdAt").orderBy("time")...のように複数条件で絞ると無効になってしまうためreact側で日付のfilterを行う
  const rankData = (data: Record[]): Record[] => {
    return data.map((d, i) => {
      return {
        ...d,
        rank: i + 1,
      };
    });
  };

  const today = useMemo(() => {
    return new Date();
  }, []);

  const getTwelve = (date: Date, isAm?: boolean) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = isAm ? date.getDate() : date.getDate() + 1;
    return new Date(`${year}-${month}-${day} 00:00:00`);
  };

  const formatData = useCallback(
    (data: Record[]) => {
      const filterData: Record[] =
        category === "daily"
          ? data.sort((a: Record, b: Record) => {
              if (a.time < b.time) {
                return -1;
              } else {
                return 1;
              }
            })
          : data;
      const formatData: Record[] = rankData(filterData);
      //同率の検索
      for (let i = 0; i < formatData.length; i++) {
        const j = i + 1 === formatData.length ? i : i + 1;
        if (formatData[i].time === formatData[j].time) {
          formatData[j].rank = formatData[i].rank;
        } else {
          formatData[j].rank = formatData[i].rank + 1;
        }
      }
      return formatData;
    },
    [category]
  );

  const ref = useMemo(() => {
    return category === "my"
      ? db
          .collection("records")
          .orderBy("time", "asc")
          .where("level", "==", board.level)
          .where("uid", "==", user.uid)
          .limit(limit)
      : category === "daily"
      ? db
          .collection("records")
          .orderBy("createdAt", "desc")
          .where("level", "==", board.level)
          .where("createdAt", "<=", getTwelve(today, false))
          .where("createdAt", ">=", getTwelve(today, true))
          .limit(limit)
      : db
          .collection("records")
          .orderBy("time", "asc")
          .where("level", "==", board.level)
          .limit(limit);
  }, [board.level, category, today, user.uid]);

  const findNextRecord = useCallback(
    (lastRecord: any) => {
      if (!lastRecord) return 0;
      return ref
        .startAfter(lastRecord)
        .get()
        .then((querySnapshot) => {
          return querySnapshot.docs.length;
        });
    },
    [ref]
  );

  const getData = (querySnapshot: firebase.firestore.QuerySnapshot) => {
    const data: Record[] = querySnapshot.docs.map((doc, i) => {
      const timestamp = doc.get("createdAt").toDate();

      return {
        ...(doc.data() as Record),
        createdAt: dayjs(timestamp).format("YYYY-MM-DD"),
      };
    });
    return data;
  };

  useEffect(() => {
    setState({
      records: [],
      currentRecords: [],
      lastRecord: undefined,
      currentPage: 1,
    });
  }, [category]);

  //初期データの取得
  useEffect(() => {
    let unMounted = false;
    if (!user.name) {
      history.push("/");
    }

    ref.get().then(async (querySnapshot) => {
      const data: Record[] = await getData(querySnapshot);
      const newData: Record[] = await formatData(data);
      const lastRecord: any = await querySnapshot.docs[
        querySnapshot.docs.length - 1
      ];
      const nextRecord = await findNextRecord(lastRecord);

      if (!unMounted) {
        setState({
          records: newData,
          currentRecords: newData,
          lastRecord: nextRecord === 0 ? undefined : lastRecord,
          currentPage: 1,
        });
      }
    });
    return () => {
      unMounted = true;
    };
  }, [ref, formatData, user.name, history, findNextRecord]);

  const handleClickPrev = () => {
    const offset = (currentPage - 2) * limit;
    setState({
      ...state,
      currentRecords: records.slice(offset, offset + limit),
      currentPage: currentPage - 1,
    });
  };

  const handleClickNext = async (lastRecord: any) => {
    const offset = currentPage * limit;

    if (currentPage !== Math.ceil(records.length / limit)) {
      setState({
        ...state,
        currentRecords: records.slice(offset, offset + limit),
        currentPage: currentPage + 1,
      });
      return;
    }
    //取得データの末尾に進んだ場合
    await ref
      .startAfter(lastRecord)
      .get()
      .then(async (querySnapshot) => {
        const data: Record[] = await getData(querySnapshot);
        const newData: Record[] = await formatData([...records, ...data]);
        const lastRecord: any = await querySnapshot.docs[
          querySnapshot.docs.length - 1
        ];
        const nextRecord = await findNextRecord(lastRecord);

        setState({
          ...state,
          records: newData,
          currentRecords: newData.slice(offset, offset + limit),
          //最大データ数が6で割れる場合は次へを押せないようにする
          lastRecord: nextRecord === 0 ? undefined : lastRecord,
          currentPage: currentPage + 1,
        });
      });
  };

  const _props = {
    user,
    category,
    lastRecord,
    handleClickPrev,
    handleClickNext,
    nextDisabled,
    prevDisabled,
  };

  return <Ranking data={currentRecords} {..._props} />;
};

export default ContainerRanking;
